import { Layers } from "appworks/graphics/layers/layers";
import { LayerNames } from "game-layers";
import { CanvasService } from "appworks/graphics/canvas/canvas-service";
import { GraphicsService } from "appworks/graphics/graphics-service";
import { Services } from "appworks/services/services";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { gameiomModel } from "gameiom/model/gameiom-model";
import { AbstractComponent } from "appworks/components/abstract-component";
import { Signal, SignalBinding } from "signals";
import { slotDefinition } from "slotworks/model/slot-definition";
import { uiFlags, UIMenuFlag } from "appworks/ui/flags/ui-flags";
import { Components } from "appworks/components/components";
import { AgsHeaderComponent } from "ags/components/ags-header-component";
import { Timer } from "appworks/utils/timer";
import { SoundService } from "appworks/services/sound/sound-service";

export type PaytableRowData = {
    payout: number;
    symbolId: string;
}

interface IStyleProp {
    props: string[];
    value: number;
}

declare global {
    interface Window {
        closeRules: () => void;
    }
}

export class HtmlRulesComponent extends AbstractComponent {
    private static scrollBarWidth: number = -1;
    private static readonly standardWidth: number = 1920;
    private static readonly sizeMap = new Map<string, IStyleProp>([
        [".text-size-20", {props: ['fontSize'], value: 20}],
        [".text-size-30", {props: ['fontSize'], value: 30}],
        [".text-size-40", {props: ['fontSize'], value: 40}],
        [".text-size-45", {props: ['fontSize'], value: 45}],
        [".text-size-50", {props: ['fontSize'], value: 50}],
        [".text-size-60", {props: ['fontSize'], value: 60}],
        [".text-size-70", {props: ['fontSize'], value: 70}],
        [".text-size-80", {props: ['fontSize'], value: 80}],
        [".text-size-90", {props: ['fontSize'], value: 90}],
        [".rules-title", {props:  ['fontSize'], value: 60}],
        [".rules-text", {props: ['fontSize'], value: 25}],
        ["#rules-bottom-left", {props: ['fontSize'], value: 20}],
        ["#rules-bottom-middle", {props: ['fontSize'], value: 30}],
        ["#rules-bottom-right", {props: ['fontSize'], value: 20}],
        [".rules-sym-height", {props: ['height'], value: 90}],
        ["#paylineswrapper", {props: ['maxWidth'], value: 1400}],
        [".payline-tablewrapper ", {props: ['width', 'height'], value: 180}]
    ]);

    protected uiLayer: Layers;
    protected contentListener: SignalBinding;
    protected onGameLoaded: Signal;
    protected clockTimer: number;
    protected numRowsPerPaytable: number = 4;

    constructor(numRowsPerPaytable: number = 4) {
        super();
        this.numRowsPerPaytable = numRowsPerPaytable;
        HtmlRulesComponent.scrollBarWidth = HtmlRulesComponent.getScrollbarWidth();
        this.uiLayer = Layers.get(LayerNames.MenusContent);
        this.onGameLoaded = Layers.onGameLoaded;

        const updateClock = () => {
            document.getElementById('clock-text').textContent = Components.get(AgsHeaderComponent).getClockTime();
        };

        if (gameiomModel.displayClock) {
            this.clockTimer = Timer.setInterval(updateClock, 1000);
        }

        this.uiLayer.onSceneEnter.add((scene: string) => {
            if (scene === "paytable") {
                this.show();
                // Hide MenusContent buttons which we're not using
                this.uiLayer.getFlexiButton("next").setVisible(false);
                this.uiLayer.getFlexiButton("previous").setVisible(false);
                this.uiLayer.getFlexiButton("close").setVisible(false);

            }
        });

        this.uiLayer.beforeSceneExit.add((scene: string) => {
            if (scene === "paytable") {
                this.hide();
            }
        });

        this.translateRulesDomElements();
        Services.get(CanvasService).onResize.add(() => setTimeout(this.resize, 1)); // Without the setTimeout, it gets the wrong position after an orientation change

        this.addPaylines(gameiomModel.paylines);
        this.addPaytable(gameiomModel.paytable);

        window.closeRules = () => {
            Services.get(SoundService).customEvent("button_press_close");
            uiFlags.menu(0 as UIMenuFlag);
        };
    }

    private static getScrollbarWidth(): number {
        // Create a hidden div with overflow and a large content
        const hiddenDiv = document.createElement('div');
        hiddenDiv.style.width = '100px';
        hiddenDiv.style.height = '100px';
        hiddenDiv.style.overflow = 'scroll';
        hiddenDiv.style.visibility = 'hidden';

        const innerDiv = document.createElement('div');
        innerDiv.style.width = '200px';
        innerDiv.style.height = '200px';

        hiddenDiv.appendChild(innerDiv);
        document.body.appendChild(hiddenDiv);

        // Calculate the difference in width
        const scrollbarWidth = hiddenDiv.offsetWidth - hiddenDiv.clientWidth;

        // Remove the hidden div
        document.body.removeChild(hiddenDiv);

        return scrollbarWidth;
    }

    private static resizeRulesDomElements(canvasRect: DOMRect) {
        const overlay = document.getElementById('canvas-location-overlay');

        if (overlay) {
            overlay.style.left = canvasRect.left + 'px';
            overlay.style.width = canvasRect.width + 'px';
            overlay.style.top = canvasRect.top + 'px';
            overlay.style.height = canvasRect.height + 'px';

            const portrait = canvasRect.width < canvasRect.height;

            const widthScale = canvasRect.width / HtmlRulesComponent.standardWidth;

            for (const [key, value] of HtmlRulesComponent.sizeMap) {
                document.querySelectorAll<HTMLElement>(key).forEach(e => {
                    const landscapeSize = value.value * widthScale;
                    value.props.forEach(eachProp => {
                        e.style[eachProp] = `${portrait ? landscapeSize * 1.75: landscapeSize}px`;
                    });
                });
            }

            if (HtmlRulesComponent.scrollBarWidth < 0) {
                HtmlRulesComponent.scrollBarWidth = HtmlRulesComponent.getScrollbarWidth();
            }

            document.querySelectorAll<HTMLElement>('.rules-top-bar').forEach(e => { e.style.paddingRight = `${HtmlRulesComponent.scrollBarWidth}px`; });
        }
    }

    private resize() {
        const boundingClientRect = Services.get(CanvasService).canvas.getBoundingClientRect();
        HtmlRulesComponent.resizeRulesDomElements(boundingClientRect);
    };

    private show() {
        document.getElementById('rules-background').style.display = 'flex';
        document.querySelectorAll<HTMLElement>(".rules-item").forEach(e => e.style.display = 'block');
    }

    private hide() {
        document.querySelectorAll<HTMLElement>(".rules-item").forEach(e => e.style.display = 'none');
        document.getElementById('rules-background').style.display = 'none';
    }

    private translateRulesDomElements() {
        const graphicsService = Services.get(GraphicsService);
        const renderer = Services.get(CanvasService).renderer;
        for(const element of document.querySelectorAll<HTMLElement>('[data-translation-code]')) {
            const code = element.getAttribute('data-translation-code');
            let translation = Services.get(TranslationsService).get(code);
            translation = translation.replace(/<([^>\s]+)\s*\/?>/g, (_, tagName) => {
                if(graphicsService.hasTexture(tagName)) {
                    const sprite = graphicsService.createSprite(tagName);
                    const dataUrl = renderer.extract.canvas(sprite).toDataURL('image/png');
                    return `<img class="rules-inline rules-sym-height" src="${dataUrl}" style="max-height: ${2}em;" />`;
                }
                return `<${tagName}_texture_not_found />`;
            });
            translation = translation.replace(/\n/g, '<br/>');
            element.innerHTML = translation;
        }

        let prizeTableText = '';
        const prizeTable = gameiomModel.prizeTable;
        if(Array.isArray(prizeTable)) {
            prizeTableText = prizeTable.slice(0, -1).join(', ');
            if(prizeTable.length > 2) {
                prizeTableText += ',';
            }
            if(prizeTable.length > 1) {
                prizeTableText += ' and ';
            }
            prizeTableText += prizeTable.slice(-1);
        }
        document.querySelectorAll<HTMLElement>(".rules-text-prize-table").forEach(e => e.innerText = prizeTableText);

        let rtpNumber: string | number = Number(gameiomModel.rtp);
        if (!isNaN(rtpNumber)) {
            rtpNumber = rtpNumber.toFixed(2);
        } else {
            rtpNumber = gameiomModel.rtp;
        }
        const rtpText = Services.get(TranslationsService).get("game_info_3", { rtp: rtpNumber });
        document.querySelectorAll<HTMLElement>(".rules-text-rtp").forEach(e => e.innerText = rtpText);
    }

    protected addPaylines(paylines: number[][]) {
        const paylineHtml = paylines.map((payline, index) => `<div class="payline">
            <div class="payline-header">${index + 1}</div>
                <div class="payline-tablewrapper">
                    <table class="paylines a${payline[0] + 1} b${payline[1] + 1} c${payline[2] + 1}">
                        <tr class="row1">
                            <td class="cola"></td>
                            <td class="colb"></td>
                            <td class="colc"></td>
                        </tr>
                        <tr class="row2">
                            <td class="cola"></td>
                            <td class="colb"></td>
                            <td class="colc"></td>
                        </tr>
                        <tr class="row3">
                            <td class="cola"></td>
                            <td class="colb"></td>
                            <td class="colc"></td>
                        </tr>
                    </table>
                </div>
            </div>`)
            .join('\n');
        const paylinesWrapper = document.getElementById("paylineswrapper");
        if(paylinesWrapper) {
            paylinesWrapper.insertAdjacentHTML('afterbegin', paylineHtml);
        }
    }

    protected symbolImageMap() {
        return new Map<string, string | null>([
            ['H1DH2DH3D', 'any7-double'],
            ['H1H1D', 'H1'],
            ['H2H2D', 'H2'],
            ['H3H3D', 'H3'],
            ['H1H2H3H1DH2DH3D', 'any7'],
            ['L1L2', 'anyBar'],
            ['H1H2H3H1DH2DH3DL1L2', 'any7-anyBar'],
        ]);
    }

    protected buildPaytableHtml(data: Array<PaytableRowData>): string {
        const getImageNameForSymbol = (symbolId: string) => this.symbolImageMap().get(symbolId) || symbolId;

        const getSymbols = (rowData: PaytableRowData) => `<img src="./img/mobile/General/${getImageNameForSymbol(rowData.symbolId)}@2.25x.png"/>`.repeat(3);

        const getRow = (rowData: PaytableRowData) => `
            <tr>
                <td class="symbols">
                    ${getSymbols(rowData)}
                </td>
                <td class="amount text-size-70">
                    ${rowData.payout.toLocaleString()}
                </td>
            </tr>
        `;

        const getTable = (data: Array<PaytableRowData>) => `<table>${data.map(getRow).join('')}</table>`;

        const tables = [];
        for(let i = 0; i < data.length; i += this.numRowsPerPaytable) {
            tables.push(getTable(data.slice(i, i + this.numRowsPerPaytable)));
        }
        return `<div>${tables.join('')}</div>`;
    }

    private addPaytable(paytable: number[][]) {
        const paytableData = paytable.reduce<Array<PaytableRowData>>((accum, val, index) => {
            const symbolId = slotDefinition.symbolDefinitions[index].id;
            for(let i = 0; i < val.length; i++) {
                // TODO: this logic is only right when there's a single payout in the third position (index 2). That's all this game has.
                const payout = val[i];
                if(payout > 0) {
                    accum.push({payout, symbolId});
                }
            }
            return accum;
        }, []);

        const paytableHtml = this.buildPaytableHtml(paytableData);;
        const paytableWrapper = document.getElementById("paytablewrapper");
        if(paytableWrapper) {
            paytableWrapper.insertAdjacentHTML('afterbegin', paytableHtml);
        }
    }
}